import React, { useState } from 'react'
import PropTypes from 'prop-types'
import Modal from 'components/mui/Modal'
import Box from 'components/mui/Box'
import Typography from 'components/mui/Typography'
import AppButtonComponent from 'components/mui/AppButtonComponent'
import Checkbox from 'components/mui/Checkbox'
import { IconButton, useTheme, CircularProgress } from '@mui/material'
import { Close } from '@mui/icons-material'
import { typography } from 'theme'
import { useGetCustomerProfileQuery } from 'store/api/customer.apislice'

/**
 * Update Contact Details Modal - Allows users to select which contact details to update
 */
const UpdateContactDetailsModal = ({
  open,
  onClose,
  onProceed,
  mobileNumber: propsMobileNumber = '',
  emailAddress: propsEmailAddress = ''
}) => {
  const theme = useTheme()
  const { data: profileResponse, isLoading: isProfileLoading } = useGetCustomerProfileQuery(undefined, { skip: !open })

  const profileData = profileResponse?.profile || {}
  const mobileNumber = profileData?.mobileNumber || propsMobileNumber
  const emailAddress = profileData?.emailId || propsEmailAddress

  const [selectedFields, setSelectedFields] = useState({
    mobile: false,
    email: false
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  React.useEffect(() => {
    if (!open) {
      setSelectedFields({
        mobile: false,
        email: false
      })
      setIsSubmitting(false)
    }
  }, [open])

  const handleCheckboxChange = (field) => {
    setSelectedFields(prev => ({
      ...prev,
      [field]: !prev[field]
    }))
  }

  const handleProceed = () => {
    if (onProceed) {
      setIsSubmitting(true)
      onProceed(selectedFields)
    }
  }

  const containerSx = {
    bgcolor: '#0B1120',
    borderRadius: '16px',
    p: { xs: 2.5, sm: 3 },
    position: 'relative',
    width: { xs: '100%', sm: 480, md: 520 },
    color: '#FFFFFF',
    boxShadow: '0px 16px 48px rgba(0,0,0,0.4)',
    border: '1px solid rgba(255,255,255,0.08)'
  }

  const titleStyle = {
    fontSize: '18px',
    fontWeight: typography.fontWeight.semibold,
    color: '#FFFFFF',
    mb: 0.5,
    textAlign: 'center'
  }

  const subtitleStyle = {
    fontSize: '13px',
    color: '#A3B1CC',
    lineHeight: 1.5,
    textAlign: 'center',
    mb: 3
  }

  const contactItemStyle = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 2,
    mb: 3,
    '&:last-child': {
      mb: 0
    }
  }

  const contactLabelStyle = {
    fontSize: '13px',
    color: '#C7D1E8',
    fontWeight: typography.fontWeight.medium,
    mb: 0.5
  }

  const contactValueStyle = {
    fontSize: '15px',
    color: '#FFFFFF',
    fontWeight: typography.fontWeight.semibold,
    lineHeight: 1.4
  }

  const contactInfoStyle = {
    flex: 1
  }

  const buttonStyle = {
    width: '100%',
    backgroundColor: '#46a8dc',
    color: '#FFFFFF',
    fontWeight: typography.fontWeight.semibold,
    textTransform: 'none',
    borderRadius: '8px',
    height: 46,
    mt: 3,
    '&:hover': {
      backgroundColor: '#46a8dc'
    },
    '&:disabled': {
      backgroundColor: '#404040',
      color: '#808080'
    }
  }

  const checkboxStyle = {
    color: 'rgba(255, 255, 255, 0.7)',
    padding: '4px',
    '&.Mui-checked': {
      color: '#64B5F6'
    }
  }

  return (
    <Modal open={open} onClose={onClose} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <Box sx={containerSx}>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            color: '#D0D6E5',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          <Close />
        </IconButton>

        <Box sx={{ textAlign: 'center', mb: 2.5 }}>
          <Typography sx={titleStyle}>
            Confirm details
          </Typography>
          <Typography sx={subtitleStyle}>
            Choose which contact details you want to update.
          </Typography>
        </Box>

        {isProfileLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} sx={{ color: '#46a8dc' }} />
          </Box>
        ) : (
          <>
            {/* Mobile Number Section */}
            <Box sx={contactItemStyle}>
              <Checkbox
                checked={selectedFields.mobile}
                onChange={() => handleCheckboxChange('mobile')}
                sx={checkboxStyle}
              />
              <Box sx={contactInfoStyle}>
                <Typography sx={contactLabelStyle}>
                  Mobile Number
                </Typography>
                <Typography sx={contactValueStyle}>
                  {mobileNumber || '--'}
                </Typography>
              </Box>
            </Box>

            {/* Email ID Section */}
            <Box sx={contactItemStyle}>
              <Checkbox
                checked={selectedFields.email}
                onChange={() => handleCheckboxChange('email')}
                sx={checkboxStyle}
              />
              <Box sx={contactInfoStyle}>
                <Typography sx={contactLabelStyle}>
                  Email ID
                </Typography>
                <Typography sx={contactValueStyle}>
                  {emailAddress || '--'}
                </Typography>
              </Box>
            </Box>
          </>
        )}

        {/* Proceed Button */}
        <AppButtonComponent
          onClick={handleProceed}
          disabled={!selectedFields.mobile && !selectedFields.email}
          loading={isSubmitting}
          sx={buttonStyle}
        >
          Proceed
        </AppButtonComponent>
      </Box>
    </Modal>
  )
}

UpdateContactDetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onProceed: PropTypes.func.isRequired,
  mobileNumber: PropTypes.string,
  emailAddress: PropTypes.string
}

UpdateContactDetailsModal.defaultProps = {
  mobileNumber: '',
  emailAddress: ''
}

export default UpdateContactDetailsModal

